import { useEffect, useState, useRef, useMemo, forwardRef } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import {isEmpty, isEqual} from "lodash";

import resourceCapacityService from "@plannel/services/data/resource-capacity-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { gridPaste, nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import DateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import GridDatePicker from "@plannel/components/GridDatePicker";
import resourceService from "@plannel/services/data/resource-service";
import NumberUtil from "@plannel/utils/number-util";

const { snackBarMessageColor } = ColorUtil;
const { mpTimeUom: TIMEUOM } = CodeUtil;

const ResourceCapacity = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const CURRENT_DATE = new ZDate();
  const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - 5, 1);
  const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
  const INIT_DATE = { start: START_DATE, end: END_DATE };

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : INIT_DATE);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [resourceCapacities, setResourceCapacities] = useState(reduxViewState?.resourceCapacities ?? []);
  const [resource, setResource] = useState([]);
  const resourceMap = useRef({});

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DynamicGridDatePicker = forwardRef((props, ref) => {
  const dynamicParams = {
    dateType: props.dateType,
    maxDate: props.maxDate,
    minDate: props.minDate,
  };

  const mockedProps = {
    ...props,
    column: {
      ...props.column,
      colDef: {
        ...props.column.colDef,
        cellEditorParams: dynamicParams
      }
    }
  };

  return <GridDatePicker {...mockedProps} ref={ref} />;
});

  const dropdownResourceValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }
    return data.value ? data.value.name : "";
  }

  const gridFormatter = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  }

  const percentFormatter = (params, isExport = false) => {
    const value = params?.data?.[params?.colDef?.field];
    if (isExport) {
      return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 });
    }
    return `${NumberUtil.formatNumber(value * 100, { maximumFractionDigits: 2, minimumFractionDigits: 0 })}%`;
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const {column, node} = params;
    const colId = column.getColId();
    if (isEqual(colId, "resNm")) {
      if (!resourceMap.current[params.value?.resCd]) {
        params.data.resCd = null;
        params.data.resNm = null;
        params.data.resId = null;
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_InvalidValue")}` });
        return;
      }
      node.data.resId = resourceMap.current[params.value?.resCd].id;
      node.data.resCd = resourceMap.current[params.value?.resCd].resCd;
      node.data.resNm = resourceMap.current[params.value?.resCd].name;

    }
    DataState.setDataState(params);
  }

  const columnDefs = [
    {
      headerName: "resCd", field: "resCd", colId: "resCd",
      type: ['nonEditableColumn'],
      width: 180,
      filterType: 'string',
    },
    {
      headerName: "resName", field: "resNm", colId: "resNm", width: 180, filterType: 'string',
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditor: "agRichSelectCellEditor",
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownResourceValueFormatter,
        values: resource
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
    },
    { headerName: "startDate", field: "startDt", type: ["defaultValueParser"], cellEditor: DynamicGridDatePicker, cellEditorParams: (params) => ({ dateType: "day", maxDate: params.data.endDt ? new Date(params.data.endDt) : null }), width: 120, valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "endDate", field: "endDt", type: ["defaultValueParser"], cellEditor: DynamicGridDatePicker, cellEditorParams: (params) => ({ dateType: "day", minDate: params.data.startDt ? new Date(params.data.startDt) : null }), width: 120, valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "runTime", field: "runTime", width: 180, filterType: 'number' },
    { headerName: "overRunTime", field: "overRunTime", width: 180, filterType: 'number' },
    {
      headerName: "timeUom", field: "timeUom", width: 120,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: TIMEUOM
      },
      valueFormatter: gridFormatter,
      filterType: 'string',
      filterOption: TIMEUOM
    },
    {
      headerName: "resEfficiency", field: "efficiency",
      type: ["rightAligned"], width: 100,
      valueFormatter: percentFormatter,
      filterType: 'percent'
    },
    {
      headerName: "usageLimitRate", field: "usageLimitRate",
      type: ["rightAligned"], width: 110,
      valueFormatter: percentFormatter,
      filterType: 'percent'
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "distFlg", field: "distFlg", type: ["booleanColumn"], width: 120, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { field: "verNum", hide: true },
  ];

  const excelColumnDefs = [
    { headerName: "resCd", field: "resCd", colId: "resCd", width: 180 },
    { headerName: "resName", field: "resNm", colId: "resNm", width: 180 },
    { headerName: "startDate", field: "startDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "endDate", field: "endDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "runTime", field: "runTime", width: 180 },
    { headerName: "overRunTime", field: "overRunTime", width: 180 },
    { headerName: "timeUom", field: "timeUom", width: 180 },
    { headerName: "resEfficiency", field: "efficiency", type: ["rightAligned"], width: 100, valueGetter: params => percentFormatter(params, true) },
    { headerName: "usageLimitRate", field: "usageLimitRate", type: ["rightAligned"], width: 110, valueGetter: params => percentFormatter(params, true) },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100 },
    { headerName: "distFlg", field: "distFlg", type: ["booleanColumn"], width: 100 },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150 },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150 },
  ];

  const retrieveResource = () => {
    resourceService.getAll()
      .then(response => {
        const { results } = response.data;
        for (const res of results) {
          resourceMap.current[res.resCd] = res;
        }
        setResource(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (period?.start) {
      searchFilters.startDate = period.start;
    }

    if (period?.end) {
      searchFilters.endDate = period.end;
    }

    if (resourceSearch) {
      searchFilters.resCd = resourceSearch.resCd;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
  }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveResourceCapacitys = () => {
    const params = getRequestParams();

    resourceCapacityService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setResourceCapacities([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setResourceCapacities(results);
        setTotalPages(totalPages);

      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {
            start: period.start, end: period.end,
            resourceSearch, currentPage, pageSize, resourceOptions: resource, advancedFilters
          });

        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    retrieveResource();
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeResourceCapacity = () => {
    const workcenterIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        workcenterIds.push(data.id)
      }
    );

    if (workcenterIds.length > 0) {
      resourceCapacityService.remove(workcenterIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveResourceCapacitys();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }

  }

  const saveResourceCapacity = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      for (const row of allStateData) {
        if (row.resCd === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_FieldRequired")}` });
          return;
        }
        if (row.efficiency < 0 || row.efficiency > 1) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resEfficiency")}] ${t("MSG_PercentLimit")}` });
          return;
        }
        if (row.usageLimitRate < 0 || row.usageLimitRate > 1) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("usageLimitRate")}] ${t("MSG_PercentLimit")}` });
          return;
        }
        if (row?.endDt < row?.startDt) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SetEndDate") });
          return;
        }
      }

      resourceCapacityService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveResourceCapacitys();
        });


    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveResourceCapacity();
    } else if (action === "DELETE") {
      removeResourceCapacity();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveResourceCapacitys();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isEmpty(resource)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
      if (reduxViewState) {
        // 후보 목록 캐시 — viewStates 의 resource 는 "선택한 자원"을 뜻한다(ResourceFilter).
        reduxViewState.resourceOptions = resource;
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setResourceCapacities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceSearch]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveResourceCapacitys();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (period?.start && period?.end) {
      retrieveResourceCapacitys();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      resCd: null,
      resName: null,
      resId: null,
      runTime: null,
      overRunTime: null,
      timeUom: "minutes",
      activeFlg: true,
      distFlg: true,
      efficiency: 1,
      usageLimitRate: 1,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
        <Autocomplete
          options={resource}
          getOptionLabel={option => option.name}
          value={resourceSearch}
          onChange={(_, newValue) => setResourceSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.resCd === value.resCd ?? ""}
          size="small"
          sx={{ minWidth: 190, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("resource")} />
          )}
        />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        </Box>
        <Box sx={{ ml: "auto", mr: 1.5 }}>
          <ExcelExportButton title={title} service={resourceCapacityService} params={getRequestParams()} columnDefs={excelColumnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </Box>
      <Box id="production-resource-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={resourceCapacities}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResourceCapacity);
